// Période d'essai & CDD — contract-lifecycle screen (soft-island port
// of the design/Contrats.html prototype, ct-* files).
//
// Deviations from the prototype (deliberate):
// - the proto's own topbar/page links/entity pills/theme toggle/role
//   switcher/Tweaks panel are dropped — the app shell + EntityBar own
//   those; the viewer's real role decides who can record a decision
//   (BOSS/ADMIN via employees.edit) while ASSISTANT keeps the reminder
//   actions (email.send),
// - reminder state lives server-side (ContractMilestone rows) instead
//   of localStorage; « Envoyer le rappel » actually emails the
//   direction (BOSS + ADMIN) through the signed-in user's Gmail,
// - the demo "manager" recipient list becomes RH + Direction,
// - contract data comes from the Employees fiche (type de contrat,
//   fin de CDD, période d'essai) — this page tracks and decides.
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useUiStore } from '../store/ui.js';
import { toast } from '../store/toast.js';
import {
  useContracts, useContractDecision, useContractMilestoneAction, useMe,
} from '../api/hooks.js';
import { CgCount, cgFmtLong, cgToday } from '../components/conges/common.js';
import {
  cddRow, ctActive, ctDays, ctDue, ctSoon, essaiRow, stepMeta,
  type ContractKind, type ContractRow, type ReminderItem,
} from '../components/contrats/common.js';
import { CtBand, CtCard, CtRail } from '../components/contrats/parts.js';
import { CtDrawer } from '../components/contrats/Drawer.js';
import { CT_ST } from '../components/contrats/common.js';
import { cgAvColor, cgInitials, entityById } from '../components/conges/common.js';
import '../styles/contrats.css';

type Filter = 'actifs' | 'echeance' | 'tous';

export function ContratsPage() {
  const themeDark = useUiStore((s) => s.theme) === 'dark';
  const entityId = useUiStore((s) => s.currentEntityId);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: ContractKind = searchParams.get('tab') === 'cdd' ? 'cdd' : 'essai';
  const [filter, setFilter] = useState<Filter>('actifs');
  const [sel, setSel] = useState<{ kind: ContractKind; id: string } | null>(null);

  const me = useMe();
  const canDecide = (me.data?.permissions ?? []).includes('employees.edit');

  const contracts = useContracts(entityId);
  const milestone = useContractMilestoneAction();
  const decision = useContractDecision();
  const busy = milestone.isPending || decision.isPending;

  const today = contracts.data?.today ?? cgToday();
  const essais = useMemo(() => (contracts.data?.essais ?? []).map(essaiRow), [contracts.data]);
  const cdds = useMemo(() => (contracts.data?.cdds ?? []).map(cddRow), [contracts.data]);
  const all = useMemo(() => [...essais, ...cdds], [essais, cdds]);
  const pool = tab === 'essai' ? essais : cdds;

  const due = useMemo(() => ctDue(all, today), [all, today]);
  const dueTab = due.filter((r) => r.p.kind === tab);
  const soon = useMemo(() => ctSoon(all, today).filter((r) => r.p.kind === tab), [all, today, tab]);

  const setTab = (t: ContractKind) => {
    const p = new URLSearchParams(searchParams);
    if (t === 'essai') p.delete('tab'); else p.set('tab', t);
    setSearchParams(p, { replace: true });
    setSel(null);
  };

  // Selected row re-read from fresh data so drawer actions re-render.
  const selRow = sel ? (sel.kind === 'essai' ? essais : cdds).find((p) => p.employee.id === sel.id) ?? null : null;

  // ── actions ────────────────────────────────────────────────
  const act = (r: { p: ContractRow; code: string }, action: 'send' | 'done' | 'snooze') => {
    const meta = stepMeta(r.p.kind, r.code);
    milestone.mutate(
      { employeeId: r.p.employee.id, kind: r.p.kind, code: r.code as 'mi' | 'j90' | 'j30' | 'j7', action },
      {
        onSuccess: (res) => {
          if (action === 'send') toast.success(res.to ? `Rappel « ${meta.lbl} » envoyé à ${res.to}` : `Rappel « ${meta.lbl} » enregistré`);
          else if (action === 'done') toast.success(`Jalon « ${meta.lbl} » marqué comme traité`);
          else toast.success(`Rappel reporté de 7 jours`);
        },
        onError: () => toast.danger("Échec de l'action sur le rappel."),
      },
    );
  };
  const sendAll = async () => {
    const items = [...dueTab];
    try {
      for (const r of items) {
        await milestone.mutateAsync({
          employeeId: r.p.employee.id, kind: r.p.kind,
          code: r.code as 'mi' | 'j90' | 'j30' | 'j7', action: 'send',
        });
      }
      toast.success(`${items.length} rappel${items.length > 1 ? 's' : ''} envoyé${items.length > 1 ? 's' : ''} à la direction`);
    } catch {
      toast.danger("Échec de l'envoi groupé.");
    }
  };
  const decide = (p: ContractRow, k: 'confirme' | 'renouvele' | 'rompu' | 'cdi' | 'nonrenouvele', meta: { date: string; offDone: number }) => {
    decision.mutate(
      {
        employeeId: p.employee.id, kind: p.kind, decision: k, date: meta.date,
        ...(k === 'renouvele' && p.kind === 'essai' ? { months: 3 } : {}),
        ...(k === 'rompu' || k === 'nonrenouvele' ? { offDone: meta.offDone } : {}),
      },
      {
        onSuccess: () => {
          setSel(null);
          toast.success(
            k === 'renouvele'
              ? `${p.employee.name} — ${p.kind === 'essai' ? 'essai renouvelé de 3 mois' : 'CDD renouvelé'}, nouveau terme le ${cgFmtLong(meta.date)}`
              : k === 'rompu' || k === 'nonrenouvele'
                ? `${p.employee.name} — sortie enregistrée au ${cgFmtLong(meta.date)} · ${meta.offDone}/5 étapes de sortie cochées`
                : `${p.employee.name} — ${(CT_ST[k] ?? ['décision enregistrée'])[0]!.toLowerCase()} au ${cgFmtLong(meta.date)}`,
          );
        },
        onError: (err) => {
          const body = (err as { body?: { message?: string } }).body;
          toast.danger(body?.message ?? "Échec de l'enregistrement de la décision.");
        },
      },
    );
  };

  // ── derived lists ──────────────────────────────────────────
  const visible = pool
    .filter((p) => (filter === 'actifs' ? ctActive(p) : filter === 'echeance' ? ctActive(p) && ctDays(today, p.end) <= 60 : true))
    .sort((a, b) => {
      const A = ctActive(a), B = ctActive(b);
      if (A !== B) return A ? -1 : 1;
      return a.end < b.end ? -1 : 1;
    });

  const outs = pool
    .filter((p) => ['rompu', 'nonrenouvele', 'termine'].includes(p.status))
    .sort((a, b) => ((a.outcomeAt ?? a.end) < (b.outcomeAt ?? b.end) ? 1 : -1));

  // ── KPIs ───────────────────────────────────────────────────
  const actEssais = essais.filter(ctActive);
  const actCdds = cdds.filter(ctActive);
  const soon30 = (l: ContractRow[]) => l.filter((p) => ctDays(today, p.end) <= 30).length;
  const KPI = tab === 'essai'
    ? [
      { v: actEssais.length, l: "Périodes d'essai actives", s: 'renouvelables une fois', tone: 'accent' },
      { v: soon30(actEssais), l: "À moins d'un mois du terme", s: 'décision à formaliser', tone: 'warn' },
      { v: dueTab.length, l: 'Rappels à traiter', s: 'jalons mi-parcours, J-30 et J-7', tone: dueTab.length ? 'bad' : '' },
      { v: essais.filter((e) => e.status === 'confirme').length, l: 'Confirmés en CDI', s: `${essais.filter((e) => e.status === 'rompu').length} rupture${essais.filter((e) => e.status === 'rompu').length > 1 ? 's' : ''} d'essai`, tone: 'info' },
    ]
    : [
      { v: actCdds.length, l: 'CDD en cours', s: 'tous périmètres confondus', tone: 'accent' },
      { v: soon30(actCdds), l: 'Terme dans moins de 30 jours', s: 'préavis à notifier', tone: 'warn' },
      { v: dueTab.length, l: 'Rappels à traiter', s: 'jalons J-90, J-30 et J-7', tone: dueTab.length ? 'bad' : '' },
      { v: cdds.filter((c) => (c.renewals ?? 0) >= 2 && ctActive(c)).length, l: 'Au plafond de renouvellement', s: 'CDI obligatoire au prochain terme', tone: 'info' },
    ];

  const dueEssai = due.filter((r) => r.p.kind === 'essai').length;
  const dueCdd = due.filter((r) => r.p.kind === 'cdd').length;

  const openRow = (p: ContractRow) => setSel({ kind: p.kind, id: p.employee.id });
  const remOf = (code: string): { p: ContractRow; code: string } | null => (selRow ? { p: selRow, code } : null);

  return (
    <div className={'ctapp' + (themeDark ? ' theme-dark' : '')}>
      <div className="ct-scrollpage">
        <div className="ct-wrap">
          <div className="ct-hero">
            <div>
              <div className="gm-eyebrow">Suivi contractuel · Données</div>
              <h1 className="gm-title">Périodes d'essai &<br /><em>fins de contrat</em></h1>
              <p className="gm-sub">
                Chaque essai et chaque CDD est suivi jour par jour. Les rappels partent à mi-parcours, à un
                mois du terme puis à J-7 — pour qu'aucune confirmation ni aucun préavis ne passe à la trappe.
              </p>
            </div>
            {due.length > 0 && (
              <div className="ct-heroalert">
                <span className="n"><CgCount value={due.length} dec={0} /></span>
                <div className="m">
                  <b>rappel{due.length > 1 ? 's' : ''} en attente</b>
                  <i>dont {due.filter((r) => r.late > 14).length} en retard de plus de 2 semaines</i>
                </div>
                <button onClick={() => { setTab(due[0]!.p.kind); setFilter('actifs'); }}>Traiter →</button>
              </div>
            )}
          </div>

          <div className="ct-kpis">
            {KPI.map((k, i) => (
              <div key={i} className="ct-kpi">
                <span className={'ct-kpi-v ' + (k.tone ?? '')}><CgCount value={k.v} dec={0} /></span>
                <span className="ct-kpi-l">{k.l}</span>
                <span className="ct-kpi-s">{k.s}</span>
              </div>
            ))}
          </div>

          <div className="ct-ctrl">
            <div className="ct-tabs">
              <button className={tab === 'essai' ? 'on' : ''} onClick={() => setTab('essai')}>
                Périodes d'essai
                {dueEssai > 0 && <span className="bdg">{dueEssai}</span>}
              </button>
              <button className={tab === 'cdd' ? 'on' : ''} onClick={() => setTab('cdd')}>
                Fins de contrat CDD
                {dueCdd > 0 && <span className="bdg">{dueCdd}</span>}
              </button>
            </div>
            <div className="ct-spacer" />
            <div className="ct-seg">
              {([['actifs', 'En cours'], ['echeance', 'Échéance < 60 j'], ['tous', "Tout l'historique"]] as const).map(([k, l]) => (
                <button key={k} className={filter === k ? 'on' : ''} onClick={() => setFilter(k)}>{l}</button>
              ))}
            </div>
          </div>

          <div className="ct-legal">
            <span className="ic">§</span>
            {tab === 'essai'
              ? <span><b>Règle interne :</b> la durée d'essai est fixée sur la fiche employé, renouvelable <b>une seule fois</b>. Sans notification écrite avant le terme, l'embauche est réputée définitive.</span>
              : <span><b>Règle interne :</b> un CDD ne peut être renouvelé que <b>deux fois</b>. Au-delà, la relation de travail se poursuit en CDI. Le non-renouvellement se notifie au plus tard un mois avant le terme.</span>}
          </div>

          {contracts.isLoading ? (
            <div className="ct-placeholder view-enter">Chargement du suivi contractuel…</div>
          ) : (
            <div key={tab} className="view-enter">
              <CtRail due={dueTab} kindLabel={tab === 'essai' ? "d'essai" : 'de CDD'} busy={busy}
                onSend={(r) => act(r, 'send')} onDone={(r) => act(r, 'done')} onSnooze={(r) => act(r, 'snooze')}
                onOpen={openRow} onAll={sendAll} />

              {tab === 'cdd' && <CtBand list={cdds} today={today} onOpen={openRow} />}

              {soon.length > 0 && (
                <div className="ct-rail plain">
                  <div className="ct-rail-h"><b>Prochains rappels programmés</b><i>dans les 30 jours</i></div>
                  {soon.slice(0, 4).map((r: ReminderItem) => (
                    <div className="ct-rem" key={r.key} onClick={() => openRow(r.p)} style={{ cursor: 'pointer' }}>
                      <span className="ct-rem-ic" style={{ background: 'var(--s2)', color: 'var(--ink3)' }}>◷</span>
                      <div className="ct-rem-m">
                        <b>{r.p.employee.name} <em>— {r.meta.lbl}</em></b>
                        <i>
                          {r.state === 'snoozed'
                            ? `Reporté au ${cgFmtLong(r.ms.state!.snoozeUntil!)}`
                            : `Rappel prévu le ${cgFmtLong(r.date)}`} · {r.meta.to}
                        </i>
                      </div>
                      <span className="ct-chip">dans {ctDays(today, r.state === 'snoozed' ? r.ms.state!.snoozeUntil! : r.date)} j</span>
                    </div>
                  ))}
                </div>
              )}

              {outs.length > 0 && (
                <div className="ct-rail plain">
                  <div className="ct-rail-h"><b>Sorties</b><i>fins de collaboration — dernier jour travaillé et formalités de sortie</i></div>
                  {outs.map((p) => {
                    const d = p.dateSortie ?? p.outcomeAt ?? p.end;
                    const fut = d >= today;
                    const ent = entityById(p.employee.entityId);
                    return (
                      <div className="ct-rem" key={p.employee.id} onClick={() => openRow(p)} style={{ cursor: 'pointer' }}>
                        <span className="gm-av" style={{ background: cgAvColor(p.employee.id), width: 34, height: 34, borderRadius: 11 }}>
                          {cgInitials(p.employee.name)}
                        </span>
                        <div className="ct-rem-m">
                          <b>{p.employee.name} <em>— {(CT_ST[p.status] ?? [])[0]}</em></b>
                          <i>
                            {p.employee.department ?? p.employee.code}{ent ? ` · ${ent.short}` : ''} · dernier jour
                            travaillé le {cgFmtLong(d)}{fut ? ` · dans ${ctDays(today, d)} j` : ''}
                          </i>
                        </div>
                        <span className={'ct-chip ' + (fut ? 'warn' : '')}>{fut ? 'sortie à préparer' : 'sortie effectuée'}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="ct-listhead">
                <div className="ct-secttl">
                  {`${tab === 'essai' ? "Périodes d'essai" : 'Contrats à durée déterminée'} · ${visible.length} contrat${visible.length > 1 ? 's' : ''}`}
                </div>
              </div>
              <div className="ct-list">
                {visible.length === 0 && (
                  <div className="ct-empty">
                    <b>Rien à afficher</b>
                    {tab === 'essai'
                      ? "Aucun contrat ne correspond à ce filtre. Les périodes d'essai se renseignent sur la fiche employé (section Cycle de vie)."
                      : 'Aucun contrat ne correspond à ce filtre. Les CDD se renseignent sur la fiche employé (type de contrat + fin de CDD).'}
                  </div>
                )}
                {visible.map((p, i) => (
                  <CtCard key={p.employee.id} p={p} today={today} i={i} onOpen={() => openRow(p)} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {selRow && (
        <CtDrawer p={selRow} today={today} canDecide={canDecide} busy={busy}
          onClose={() => setSel(null)}
          onSend={(code) => { const r = remOf(code); if (r) act(r, 'send'); }}
          onDone={(code) => { const r = remOf(code); if (r) act(r, 'done'); }}
          onSnooze={(code) => { const r = remOf(code); if (r) act(r, 'snooze'); }}
          onDecide={(k, meta) => decide(selRow, k, meta)} />
      )}
    </div>
  );
}
